import * as React from 'react';
import {
  useMediaQuery,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableBody,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import EditIcon from '@mui/icons-material/Edit';
import MUIDataTableWrapper from 'components/MUIDataTableWrapper/MUIDataTableWrapper';

// Import Zustand store
import { useAppStore } from '../../stores';

interface Revision {
  auditId: number;
  createTimestamp: string;
  objectType: string;
  detail: string;
  attendee?: {
    firstName: string;
    lastName: string;
  };
  affiliate?: {
    affiliateName: string;
  };
  user: {
    firstName: string;
    lastName: string;
  };
}

interface IAuditTableProps {
  onRowClick?: (rowData: unknown) => void;
  isAttendee?: boolean;
}

const AuditTable: React.FC<IAuditTableProps> = (props) => {
  const displayMobile = useMediaQuery('(max-width:959px)');

  // Zustand store
  const revisions = useAppStore(
    (state) => state.auditAndHistoryRevisions.revisions
  ) as Revision[];

  const columns = [
    {
      name: 'auditId',
      label: 'Audit ID',
      options: {
        display: 'excluded' as const,
      },
    },
    {
      name: 'createTimestamp',
      label: 'Date',
    },
    {
      name: 'objectType',
      label: 'Object Type',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'detail',
      label: 'Summary',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'attendeeName',
      label: 'Attendee Name',
      options: {
        display: props.isAttendee ? ('true' as const) : ('false' as const),
      },
    },
    {
      name: 'affiliate',
      label: 'Affiliate',
      options: {
        display: props.isAttendee ? ('true' as const) : ('false' as const),
      },
    },
    {
      name: 'editor',
      label: 'Editor',
    },
  ];

  const editButtonOnClick = (e: React.MouseEvent, rowData: unknown) => {
    if (props.onRowClick) {
      props.onRowClick(rowData);
    }
  };

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <MUIDataTableWrapper
          onRowClick={props.onRowClick}
          title={'Audit and Revisions'}
          setExpandableRowContent={(rowData: unknown[] | unknown) => {
            return (
              <TableRow>
                <TableCell />
                <TableCell colSpan={3}>
                  <Table>
                    <TableBody className="inner-tbody-expandable-row">
                      <TableRow>
                        <TableCell className="tbl-col-container">
                          <div className="tbl-col-title">Edit</div>
                          <div>
                            <IconButton
                              aria-label="edit-user"
                              size="small"
                              onClick={(e) => editButtonOnClick(e, rowData)}
                              className="edit-button-expandable-rows"
                            >
                              <EditIcon />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left" className="tbl-col-container">
                          <div className="tbl-col-title">Object Type</div>
                          <div>
                            {(rowData as unknown[])[2] ? String((rowData as unknown[])[2]) : 'N/A'}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left" className="tbl-col-container">
                          <div className="tbl-col-title">Summary</div>
                          <div>
                            {(rowData as unknown[])[3] ? String((rowData as unknown[])[3]) : 'N/A'}
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            );
          }}
          options={{
            print: false,
            download: false,
            viewColumns: false,
            filter: false,
            rowsPerPageOptions: [5, 10, 15, 25],
            selectableRows: 'none',
            responsive: 'vertical',
          }}
          data={revisions.map((revision: Revision) => {
            return {
              auditId: revision.auditId,
              createTimestamp: new Date(revision.createTimestamp).toLocaleString(),
              objectType: revision.objectType,
              detail: revision.detail,
              attendeeName: revision.attendee
                ? revision.attendee.firstName + ' ' + revision.attendee.lastName
                : '',
              affiliate: revision.affiliate ? revision.affiliate.affiliateName : '',
              editor: revision.user.firstName + ' ' + revision.user.lastName,
            };
          })}
          columns={columns}
        />
      </Grid>
    </Grid>
  );
};

export default AuditTable;
